import { isIOS } from "@/lib/platform"

/**
 * Голос скраба: одна слышимая дорожка под плейхедом в момент апдейта.
 * URL обязан быть same-origin fetch-able (проксируемые ассеты / scrub_url версии) —
 * stream_url версий в проде отвечает 302 на S3 без CORS и для fetch непригоден.
 */
export type ScrubVoice = {
  url: string
  /** Позиция в исходнике, мс (с учётом скорости клипа). */
  sourceMs: number
  /** Скорость клипа — питч гранулы (varispeed, как в NLE). */
  rate: number
  /** Итоговая громкость: master × track × fade. Нулевые голоса движок не собирает. */
  gain: number
  /**
   * Полная длительность исходного файла, мс — пре-гейт декода: PCM материализует
   * весь файл, поэтому источник с неизвестной длительностью не фетчится вовсе.
   */
  durationMs?: number
}

/** Целевой sample rate декодированного PCM: mono 22.05 кГц = 88 200 Б/с (4-8× экономия). */
const SCRUB_SAMPLE_RATE = 22050
/** Длительность гранулы (wall-время) — короче слышны щелчки, длиннее «эхо» за курсором. */
const GRAIN_MS = 80
/** Минимальный wall-интервал между пачками гранул (перекрытие ~50% при 80 мс грануле). */
const SPAWN_INTERVAL_MS = 40
/** Конверт гранулы против щелчков: Chrome 64+ убрал автосглаживание AudioParam. */
const GRAIN_ATTACK_S = 0.006
const GRAIN_RELEASE_S = 0.012
/** Старт чуть впереди currentTime: рамп, запланированный «в прошлое», срезается в ступеньку. */
const SCHEDULE_AHEAD_S = 0.008
/**
 * Порог накопленного движения плейхеда между пачками. Именно накопленного: на сильном
 * зуме медленный точный драг даёт <2 мс на pointermove, per-event порог глушил бы
 * главный сценарий прицельного поиска звука.
 */
const MIN_MOVE_MS = 3
/** Одновременно звучащих дорожек (приоритет — порядок дорожек документа). */
const MAX_VOICES = 4
/** Страховка от утечки узлов при залипшем onended. */
const MAX_LIVE_GRAINS = 24

/** Бюджеты декодированного PCM: на iOS большие декоды стабильно роняют вкладку Safari. */
const CACHE_BUDGET_BYTES = isIOS() ? 24 * 1024 * 1024 : 96 * 1024 * 1024
const ITEM_BUDGET_BYTES = isIOS() ? 12 * 1024 * 1024 : 48 * 1024 * 1024
/** Байт PCM на миллисекунду источника (mono float32 на целевом rate) — для пре-гейта. */
const PCM_BYTES_PER_MS = (SCRUB_SAMPLE_RATE * 4) / 1000

type PcmEntry =
  | { state: "queued"; owners: Set<AudioScrubber> }
  | { state: "loading"; owners: Set<AudioScrubber>; controller: AbortController }
  | { state: "ready"; buffer: AudioBuffer; bytes: number; lastUsedAt: number }
  | { state: "failed" }

// Контекст и PCM-кэш — модульные: движков на странице несколько одновременно
// (редактор шота всегда в DOM плюс диалоги превью), версия декодируется один раз.
let sharedCtx: AudioContext | null = null
let ctxUnavailable = false
const pcmCache = new Map<string, PcmEntry>()
let cacheBytes = 0

/**
 * Fetch+decode строго по одному: первый драг по мультитрек-таймлайну запрашивает
 * до MAX_VOICES источников разом, а параллельные декоды складывают транзиентные
 * пики памяти (encoded + native PCM + ресемпл на каждый) и занимают несколько
 * прокси-стримов Puma одновременно.
 */
const MAX_CONCURRENT_LOADS = 1
let activeLoads = 0
const loadQueue: string[] = []

function ensureContext(): AudioContext | null {
  if (sharedCtx) return sharedCtx
  if (ctxUnavailable) return null
  try {
    sharedCtx = new AudioContext({ latencyHint: "interactive" })
  } catch {
    ctxUnavailable = true
    return null
  }
  return sharedCtx
}

/**
 * decodeAudioData ресемплит к rate своего контекста: декод на offline-22050
 * срезает транзиентный пик памяти в ~2-4 раза против живого 44.1/48k контекста.
 * Старые WebKit не принимают rate < 44100 в конструкторе — тогда декодим на живом
 * контексте (буфер тяжелее, бюджет считается по фактическим байтам).
 */
async function decodeAtScrubRate(encoded: ArrayBuffer): Promise<AudioBuffer> {
  let offline: OfflineAudioContext | null = null
  try {
    offline = new OfflineAudioContext(1, 1, SCRUB_SAMPLE_RATE)
  } catch {
    offline = null
  }
  if (offline) return await offline.decodeAudioData(encoded)
  const ctx = ensureContext()
  if (!ctx) throw new Error("Web Audio API unavailable")
  return await ctx.decodeAudioData(encoded)
}

function downmixToMono(src: AudioBuffer): AudioBuffer {
  if (src.numberOfChannels === 1) return src
  const out = new AudioBuffer({ numberOfChannels: 1, length: src.length, sampleRate: src.sampleRate })
  const mixed = out.getChannelData(0)
  for (let ch = 0; ch < src.numberOfChannels; ch += 1) {
    const data = src.getChannelData(ch)
    for (let i = 0; i < data.length; i += 1) mixed[i] += data[i]
  }
  const scale = 1 / src.numberOfChannels
  for (let i = 0; i < mixed.length; i += 1) mixed[i] *= scale
  return out
}

function evictLru() {
  if (cacheBytes <= CACHE_BUDGET_BYTES) return
  const ready = [...pcmCache.entries()]
    .filter((pair): pair is [string, Extract<PcmEntry, { state: "ready" }>] => pair[1].state === "ready")
    .sort((a, b) => a[1].lastUsedAt - b[1].lastUsedAt)
  for (const [url, entry] of ready) {
    if (cacheBytes <= CACHE_BUDGET_BYTES) break
    pcmCache.delete(url)
    cacheBytes -= entry.bytes
  }
}

/** Ленивая загрузка PCM; отказ (сеть, кодек, бюджет) терминален на сессию — без retry-штормов. */
function requestPcm(url: string, durationMs: number | undefined, owner: AudioScrubber) {
  const existing = pcmCache.get(url)
  if (existing) {
    // Загрузка уже идёт/в очереди — подписываем ещё одного владельца, чтобы его
    // отмена не оборвала загрузку, нужную другому движку (редактор + диалог превью).
    if (existing.state === "queued" || existing.state === "loading") existing.owners.add(owner)
    return
  }
  // Пре-гейт ДО fetch: decodeAudioData материализует весь файл (транзиент — стерео на
  // нативном rate), проверка бюджета после декода была бы проверкой после пика памяти.
  // Неизвестная длительность — не скрабим источник вовсе.
  if (durationMs == null) return
  if (durationMs * PCM_BYTES_PER_MS > ITEM_BUDGET_BYTES) {
    pcmCache.set(url, { state: "failed" })
    return
  }
  pcmCache.set(url, { state: "queued", owners: new Set([owner]) })
  loadQueue.push(url)
  pumpLoads()
}

function pumpLoads() {
  while (activeLoads < MAX_CONCURRENT_LOADS && loadQueue.length > 0) {
    const url = loadQueue.shift()
    if (url == null) return
    const entry = pcmCache.get(url)
    // Отменённые в очереди записи уже удалены из кэша — просто пропускаем.
    if (entry?.state !== "queued") continue
    startLoad(url, entry.owners)
  }
}

function isAbortError(err: unknown): boolean {
  return err instanceof DOMException && err.name === "AbortError"
}

function startLoad(url: string, owners: Set<AudioScrubber>) {
  const controller = new AbortController()
  pcmCache.set(url, { state: "loading", owners, controller })
  activeLoads += 1
  void (async () => {
    try {
      const res = await fetch(url, { credentials: "same-origin", signal: controller.signal })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const encoded = await res.arrayBuffer()
      // decodeAudioData отменить нельзя — но отмена между fetch и декодом срезает
      // и сам декод, ради него отмена на play/dispose и существует.
      if (controller.signal.aborted) throw new DOMException("aborted", "AbortError")
      const decoded = await decodeAtScrubRate(encoded)
      const mono = downmixToMono(decoded)
      const bytes = mono.length * 4
      if (bytes > ITEM_BUDGET_BYTES) throw new Error(`decoded PCM over budget: ${bytes} bytes`)
      pcmCache.set(url, { state: "ready", buffer: mono, bytes, lastUsedAt: performance.now() })
      cacheBytes += bytes
      evictLru()
    } catch (err) {
      if (isAbortError(err)) {
        // Отмена не терминальна: запись убираем целиком, следующий скраб перезапросит.
        pcmCache.delete(url)
      } else {
        console.warn("audio scrub: source unavailable", url, err)
        pcmCache.set(url, { state: "failed" })
      }
    } finally {
      activeLoads -= 1
      pumpLoads()
    }
  })()
}

/**
 * Отписывает движок от его незавершённых загрузок; загрузка без владельцев
 * отменяется (fetch — abort, очередь — удаление). Зовётся на play/dispose:
 * скачивание и декод не должны конкурировать за сеть и CPU с реальным плеером.
 */
function cancelPcmLoads(owner: AudioScrubber) {
  for (const [url, entry] of pcmCache) {
    if (entry.state !== "queued" && entry.state !== "loading") continue
    entry.owners.delete(owner)
    if (entry.owners.size > 0) continue
    if (entry.state === "loading") entry.controller.abort()
    else pcmCache.delete(url)
  }
}

type LiveGrain = { source: AudioBufferSourceNode; gain: GainNode; startAt: number }

/**
 * Слышимый скраб: короткие гранулы Web Audio поверх декодированного mono-PCM,
 * как в DAW. Движок кормит update() из своего scrub-rAF; звук не зависит от пула
 * media-элементов (те стоят на паузе и дают кадр). Любой отказ — тихая деградация
 * до сегодняшнего немого скраба, per-источник.
 */
export class AudioScrubber {
  private live = new Set<LiveGrain>()
  private lastTimelineMs: number | null = null
  private pendingMoveMs = 0
  private lastSpawnAt = 0

  /**
   * Синхронно из pointer-обработчика (scrubTo): создаёт/будит AudioContext.
   * Из rAF-колбэка нельзя — Safari не считает его стеком пользовательского жеста.
   */
  ensureRunning() {
    const ctx = ensureContext()
    if (ctx && ctx.state !== "running") void ctx.resume().catch(() => {})
  }

  /** Каждый scrub-кадр движка: голоса слышимых дорожек под плейхедом. */
  update(timelineMs: number, voices: ScrubVoice[]) {
    const active = voices.slice(0, MAX_VOICES)
    for (const voice of active) requestPcm(voice.url, voice.durationMs, this)

    const ctx = sharedCtx
    if (!ctx || ctx.state !== "running") {
      this.lastTimelineMs = timelineMs
      this.pendingMoveMs = 0
      return
    }
    // Первый апдейт жеста — только фиксация позиции: клик по линейке без драга
    // прыгает на секунды и не должен издавать «блип» (клик ≠ скраб).
    if (this.lastTimelineMs == null) {
      this.lastTimelineMs = timelineMs
      return
    }
    this.pendingMoveMs += Math.abs(timelineMs - this.lastTimelineMs)
    this.lastTimelineMs = timelineMs
    if (active.length === 0) return

    const now = performance.now()
    if (now - this.lastSpawnAt < SPAWN_INTERVAL_MS || this.pendingMoveMs < MIN_MOVE_MS) return
    // Слот и накопленное движение сгорают только на реальном звуке: пока PCM грузится,
    // «пустые» кадры не съедают их — иначе после декода пришлось бы двигать плейхед
    // ещё раз, и click-hold без дальнейшего драга оставался бы немым.
    let spawned = false
    for (const voice of active) {
      if (this.spawnGrain(ctx, voice)) spawned = true
    }
    if (spawned) {
      this.lastSpawnAt = now
      this.pendingMoveMs = 0
    }
  }

  /** Отменяет незавершённые fetch+decode этого движка (см. cancelPcmLoads). */
  cancelLoads() {
    cancelPcmLoads(this)
  }

  /** Конец жеста: короткий рамп-аут живых гранул, чтобы обрыв не щёлкал. */
  stop() {
    this.lastTimelineMs = null
    this.pendingMoveMs = 0
    const ctx = sharedCtx
    if (!ctx) return
    const t = ctx.currentTime
    for (const grain of this.live) {
      try {
        const param = grain.gain.gain
        if (t < grain.startAt) {
          // Гранула ещё не стартовала: глушим целиком. Отменять конверт и читать
          // .value нельзя — после cancel он вернул бы интринсик GainNode = 1.0,
          // и «рамп-аут» дал бы всплеск полной громкости.
          param.cancelScheduledValues(0)
          param.setValueAtTime(0, t)
          grain.source.stop(t)
        } else {
          // Уровень читаем ДО cancelScheduledValues: отмена удаляет незавершённый
          // release-рамп, и вычисляемое значение отскакивает вверх к voice.gain —
          // рамп-аут стартовал бы со скачка (щелчок, ради которого он и написан).
          const level = param.value
          param.cancelScheduledValues(t)
          param.setValueAtTime(level, t)
          param.linearRampToValueAtTime(0, t + GRAIN_RELEASE_S)
          grain.source.stop(t + GRAIN_RELEASE_S + 0.005)
        }
      } catch {
        // Источник уже завершился сам — onended вычистит узлы.
      }
    }
    this.live.clear()
  }

  /** true — гранула реально запланирована (PCM готов и позиция внутри буфера). */
  private spawnGrain(ctx: AudioContext, voice: ScrubVoice): boolean {
    const entry = pcmCache.get(voice.url)
    if (entry?.state !== "ready") return false
    entry.lastUsedAt = performance.now()

    const offsetS = voice.sourceMs / 1000
    if (offsetS < 0 || offsetS >= entry.buffer.duration) return false
    const rate = Math.min(Math.max(voice.rate, 1 / 16), 16)

    const source = ctx.createBufferSource()
    source.buffer = entry.buffer
    source.playbackRate.value = rate
    const gain = ctx.createGain()
    const t0 = ctx.currentTime + SCHEDULE_AHEAD_S
    const durS = GRAIN_MS / 1000
    // Компенсация overlap-add: соседние гранулы перекрываются на ~50%, и при скрабе
    // около 1x их содержимое фазово-когерентно — без ×(интервал/длительность) сустейны
    // суммировались бы в +6 дБ с клиппингом ровно в главном сценарии «прослушать реплику».
    const level = voice.gain * (SPAWN_INTERVAL_MS / GRAIN_MS)
    gain.gain.setValueAtTime(0, t0)
    gain.gain.linearRampToValueAtTime(level, t0 + GRAIN_ATTACK_S)
    gain.gain.setValueAtTime(level, t0 + durS - GRAIN_RELEASE_S)
    gain.gain.linearRampToValueAtTime(0, t0 + durS)
    source.connect(gain)
    gain.connect(ctx.destination)

    const grain: LiveGrain = { source, gain, startAt: t0 }
    this.live.add(grain)
    if (this.live.size > MAX_LIVE_GRAINS) {
      const oldest = this.live.values().next().value
      if (oldest) {
        try {
          oldest.source.stop()
        } catch {
          // уже остановлен
        }
        this.live.delete(oldest)
      }
    }
    source.onended = () => {
      this.live.delete(grain)
      source.disconnect()
      gain.disconnect()
    }
    // duration аргумента start() — в секундах контента буфера: rate× даёт GRAIN_MS wall-времени.
    source.start(t0, offsetS, durS * rate)
    return true
  }
}
